/**
 * Display formatters shared by the Team Leader exam workspace.
 *
 * These were duplicated verbatim in `exams_.$examId.tsx` and `exams_.student.$studentId.tsx`
 * (each route file in this codebase historically defined its own). Splitting the workspace into
 * tab components would have multiplied those copies by five, so they live in one place — beside
 * `exam-status-labels.ts`, which is already the single source for status *wording*.
 *
 * Formatting only. No domain rules, no authorization, no status vocabulary.
 */

export function formatScore(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : `${Math.round(v)}%`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function formatDate(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Date only — used for the exam's scheduling window, where the time of day is noise. */
export function formatDay(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * The exam's scheduling window as one string. Renders whatever is present: an exam may carry only
 * a start, only an end, or neither, and the header must not print "— – —" for the last case.
 */
export function formatExamWindow(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  if (!start && !end) return null;
  if (start && end) return `${formatDay(start)} – ${formatDay(end)}`;
  if (start) return `From ${formatDay(start)}`;
  return `Until ${formatDay(end)}`;
}

export function initialsOf(name: string | null | undefined, email: string): string {
  const source = (name || email || "").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}
